import { useState } from "react";
import { Search, ArrowUpRight, ArrowDownLeft, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// FIN-2 — ExtratoToolbar (T8)
// Linha 1: search (debounce no hook) + toggle 3-estado + Exportar.
// Linha 2: 4 dropdowns (T9 — slot pre-renderizado).

function TypeToggle({ value, onChange }) {
  const PILLS = [
    { id: "entrada", label: "Entrada", icon: ArrowDownLeft, tone: "emerald" },
    { id: "saida",   label: "Saída",   icon: ArrowUpRight,  tone: "error" },
  ];
  const TONE_ACTIVE = {
    emerald: "bg-brand-green/15 border-brand-green/40 text-brand-green",
    error:   "bg-status-error/15 border-status-error/40 text-status-error",
  };

  // Toggle 3-estado: clique numa pill ativa só ela. Click na pill ativa a
  // desativa (volta pra "all"). Clicar na outra troca.
  const handleClick = (id) => {
    if (value === id) {
      onChange("all");
    } else {
      onChange(id);
    }
  };

  return (
    <div className="inline-flex gap-1.5" role="group" aria-label="Filtro tipo">
      {PILLS.map(p => {
        const Icon = p.icon;
        const isActive = value === p.id;
        return (
          <button
            key={p.id}
            type="button"
            aria-pressed={isActive}
            onClick={() => handleClick(p.id)}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-caption font-medium border transition-all duration-fast ease-smooth",
              isActive
                ? TONE_ACTIVE[p.tone]
                : "bg-transparent border-subtle text-foreground-muted hover:bg-surface-card-hover hover:text-foreground",
            )}
            data-testid={`extrato-toggle-${p.id}`}
          >
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

export default function ExtratoToolbar({
  filters,
  setFilter,
  exportCsv,
  filtersRow,           // slot pra dropdowns (T9)
  hasActiveFilters,
  onResetFilters,
}) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const out = await exportCsv();
      toast.success(`Exportado: ${out.filename}`);
    } catch (e) {
      toast.error(e.message || "Falha ao exportar extrato");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      className="space-y-3"
      data-testid="extrato-toolbar"
    >
      {/* Linha 1 — search + toggle + export */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted pointer-events-none" />
          <Input
            type="search"
            inputMode="search"
            placeholder="Buscar transação..."
            value={filters.q || ""}
            onChange={(e) => setFilter("q", e.target.value)}
            className="pl-9"
            aria-label="Buscar transação"
            data-testid="extrato-search-input"
          />
        </div>

        <div className="flex items-center justify-between md:justify-end gap-3">
          <TypeToggle
            value={filters.type || "all"}
            onChange={(v) => setFilter("type", v)}
          />

          <Button
            variant="secondary"
            size="md"
            onClick={handleExport}
            disabled={exporting}
            data-testid="extrato-export-btn"
            className="shrink-0"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="w-4 h-4" aria-hidden="true" />
            )}
            <span className="hidden md:inline">Exportar extrato</span>
          </Button>
        </div>
      </div>

      {/* Linha 2 — slot dropdowns (T9) */}
      {filtersRow ? (
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          {filtersRow}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="text-caption text-foreground-muted underline-offset-2 hover:underline"
              data-testid="extrato-reset-filters"
            >
              Limpar filtros
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
